import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { configureStatusBar } from "../../lib/statusBar";
import {
  ChevronLeft, User, Trash2, FileText, ShieldCheck, LogOut, Info,
} from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { getTenantSlug } from "../../lib/tenant";
import { BACKEND_URL } from "../../lib/backendUrl";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { toast } from "sonner";

/**
 * Profile row used by RiderProfile — top-level so React doesn't unmount
 * the subtree on every parent render (eslint react/no-unstable-nested-components).
 */
function ProfileRow({ icon: Icon, label, sub, onClick, href, danger, testId }) {
  const cls = `w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl px-5 py-4 flex items-center gap-4 transition-colors ${danger ? "text-brand-clay" : "text-white"}`;
  const inside = (
    <>
      <div className={`w-10 h-10 rounded-2xl ${danger ? "bg-brand-clay/15" : "bg-white/10"} inline-flex items-center justify-center shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 text-left min-w-0">
        <div className="font-bold text-sm">{label}</div>
        {sub && <div className="text-xs text-white/50 truncate">{sub}</div>}
      </div>
    </>
  );
  return href
    ? <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testId}>{inside}</a>
    : <button onClick={onClick} className={cls} data-testid={testId}>{inside}</button>;
}

/**
 * Rider Profile — destination for the per-account links Apple / Play Store
 * reviewers expect: account deletion (5.1.1(v)), the per-tenant Privacy &
 * Terms URLs, the build version, and the logout button.
 *
 * Layout is deliberately spartan to match the dark rider aesthetic and keep
 * thumb reach on a one-handed phone: every action is a wide tap-target row.
 */
export default function RiderProfile() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const tenantSlug = getTenantSlug();
  const apiBase = BACKEND_URL || "";
  useEffect(() => { configureStatusBar({ bgColor: "#111827", style: "light" }); }, []);
  // Per-tenant per-app legal URLs (rider persona) — match what the Super
  // Admin pastes into Play Console for this specific app listing.
  const privacyUrl = `${apiBase}/api/legal/rider/${tenantSlug}/privacy`;
  const termsUrl = `${apiBase}/api/legal/rider/${tenantSlug}/terms`;
  const appVersion = process.env.REACT_APP_VERSION || "1.0.0";

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);

  return (
    <div className="min-h-screen pt-safe bg-brand-ink text-white">
      <div aria-hidden className="fixed top-0 left-0 right-0 z-40 bg-brand-ink pointer-events-none"
           style={{ height: "env(safe-area-inset-top, 0px)" }}/>
      <header className="px-4 py-3 flex items-center gap-3 border-b border-white/10 sticky top-0 z-20 bg-brand-ink/95 backdrop-blur">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-2xl bg-white/10 inline-flex items-center justify-center" data-testid="rider-profile-back">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="font-heading text-xl font-bold">Profile</h1>
      </header>

      <main className="px-5 py-6 space-y-3 max-w-md md:max-w-2xl mx-auto pb-12">
        {/* Identity card */}
        <section className="bg-white/5 border border-white/10 rounded-3xl p-5 flex items-center gap-4" data-testid="rider-identity-card">
          <div className="w-14 h-14 rounded-2xl bg-brand-clay inline-flex items-center justify-center text-2xl font-heading font-extrabold">
            {(user?.name || "R")[0].toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-heading text-xl font-bold truncate" data-testid="rider-profile-name">{user?.name || "Rider"}</div>
            <div className="text-sm text-white/60 font-mono" data-testid="rider-profile-phone">{user?.phone}</div>
          </div>
        </section>

        {/* Action rows */}
        <ProfileRow icon={ShieldCheck} label="Privacy Policy" sub="Read how your data is handled" href={privacyUrl} testId="rider-privacy-link" />
        <ProfileRow icon={FileText}   label="Terms & Conditions" sub="Service agreement"        href={termsUrl}   testId="rider-terms-link" />
        <ProfileRow icon={Info}       label="App Version"          sub={appVersion}                  testId="rider-app-version" />
        <ProfileRow icon={Trash2}     label="Delete my account"    sub="Soft-delete · keeps audit"    onClick={() => setDeleteOpen(true)} danger testId="rider-delete-account-row" />
        <ProfileRow icon={LogOut}     label="Logout"               sub="Sign out of this device"      onClick={logout} testId="rider-profile-logout" />

        <p className="text-center text-[11px] text-white/30 pt-4">Need help? Contact your dispatcher.</p>
      </main>

      {/* Delete account dialog — same contract as the customer flow */}
      <Dialog open={deleteOpen} onOpenChange={(o) => { setDeleteOpen(o); if (!o) setDeleteConfirm(""); }}>
        <DialogContent className="max-w-sm rounded-3xl bg-brand-cream text-brand-ink" data-testid="rider-delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-brand-clay">Delete your rider account?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>You&rsquo;ll be signed out and your personal details (name, phone, email, GPS, push tokens) will be removed.</p>
            <p>For payroll &amp; audit we keep your past delivery history — those rows will show as <b>Deleted Rider</b>.</p>
            <p>You&rsquo;ll be unassigned from every route. <b>This can&rsquo;t be undone.</b></p>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Type <b>DELETE</b> to confirm</label>
              <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder="DELETE"
                     className="h-12 rounded-2xl font-mono mt-2 bg-white" data-testid="rider-delete-confirm-input" />
            </div>
          </div>
          <Button
            onClick={async () => {
              if (deleteConfirm !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
              setDeleteBusy(true);
              try {
                await api.delete("/api/delivery/me/account");
                toast.success("Account deleted. Signing you out…");
                setTimeout(() => logout(), 800);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Couldn't delete account");
              } finally {
                setDeleteBusy(false);
              }
            }}
            disabled={deleteBusy || deleteConfirm !== "DELETE"}
            className="w-full h-12 rounded-2xl bg-brand-clay hover:bg-orange-700 font-bold"
            data-testid="rider-confirm-delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> {deleteBusy ? "Deleting…" : "Yes, delete my account"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
